/**
 * creature/genome.ts — The Soul of a Creature
 *
 * The behavioral genome defines a creature's personality — not as a label,
 * but as a continuous landscape of tendencies. A genome with high curiosity
 * and low obedience produces a creature that explores everything and comes
 * when called... eventually. Maybe. If it feels like it.
 *
 * Genomes are heritable through breeding (src/breeding/genetics.ts) and
 * drift slightly through lived experience. The defaults come from the
 * species template; each individual varies.
 */

import type { BehaviorGenomeDefaults } from "./species.js";

// ─── Behavior Genome ────────────────────────────────────────────────────────

/** Runtime behavioral genome for a specific creature instance */
export interface BehaviorGenome {
  // Personality axes (0-1 continuous)
  energy: number;         // 0 = sleepy, 1 = hyperactive
  sociability: number;    // 0 = aloof, 1 = clingy
  curiosity: number;      // 0 = stays put, 1 = explores everything
  obedience: number;      // 0 = independent, 1 = follows commands
  playfulness: number;    // 0 = serious, 1 = always plays
  courage: number;        // 0 = skittish, 1 = bold

  // Physical traits
  agility: number;        // affects turn speed, jump distance
  stamina: number;        // how long before needing rest

  // Schedule modifiers
  nocturnality: number;   // 0 = diurnal, 1 = nocturnal

  // Social modifiers
  territoriality: number; // how defensive of home tiles
  packInstinct: number;   // tendency to cluster with same-species
}

// ─── Factory ────────────────────────────────────────────────────────────────

/** Create a genome from species defaults with optional random variation */
export function createGenome(
  defaults: BehaviorGenomeDefaults,
  variance: number = 0.1
): BehaviorGenome {
  return {
    energy: vary(defaults.energy, variance),
    sociability: vary(defaults.sociability, variance),
    curiosity: vary(defaults.curiosity, variance),
    obedience: vary(defaults.obedience, variance),
    playfulness: vary(defaults.playfulness, variance),
    courage: vary(defaults.courage, variance),
    agility: vary(defaults.agility, variance),
    stamina: vary(defaults.stamina, variance),
    nocturnality: vary(defaults.nocturnality, variance),
    territoriality: vary(defaults.territoriality, variance),
    packInstinct: vary(defaults.packInstinct, variance),
  };
}

/** Clone a genome */
export function cloneGenome(genome: BehaviorGenome): BehaviorGenome {
  return { ...genome };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Apply Gaussian-ish variation to a trait, clamped to 0-1 */
function vary(base: number, variance: number): number {
  // Box-Muller for approximate Gaussian
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(Math.max(u1, 0.0001))) * Math.cos(2 * Math.PI * u2);
  return clamp01(base + z * variance);
}

/** Clamp to [0, 1] */
function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Compute how active the creature should be given time of day and nocturnality */
export function activityLevel(genome: BehaviorGenome, gameHour: number): number {
  // Day creatures peak at noon (12), night creatures peak at midnight (0/24)
  const hourAngle = (gameHour / 24) * Math.PI * 2;
  const dayActivity = Math.cos(hourAngle) * 0.5 + 0.5; // 1 at noon, 0 at midnight
  const nightActivity = 1 - dayActivity;
  const activity = dayActivity * (1 - genome.nocturnality) + nightActivity * genome.nocturnality;
  return activity * genome.energy;
}
